import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/wms/api')

export type InboundStatus =
  | 'draft'
  | 'released'
  | 'receiving'
  | 'received'
  | 'closed'
  | 'cancelled'

export type PutawayStatus = 'pending' | 'done'

export interface Asn {
  id: number
  tenant_id: number
  doc_no: string
  warehouse_id: number
  supplier_id: number
  expected_at: string | null
  status: InboundStatus
  remark: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface AsnItem {
  id: number
  tenant_id: number
  asn_id: number
  sku_id: number
  expected_qty: number
  received_qty: number
  created_at: string
  updated_at: string
}

/** 列表行：单据 + 仓库名/供应商名 + 应收已收汇总，一次请求省掉三次回查 */
export interface ListRow extends Asn {
  warehouse_name: string
  supplier_name: string
  total_qty: number
  received_qty: number
}

export interface ItemDetail extends AsnItem {
  sku_code: string
  sku_name: string
  unit: string
  batch_managed: boolean
}

export interface PutawayTask {
  id: number
  tenant_id: number
  task_no: string
  asn_id: number
  receipt_item_id: number
  sku_id: number
  batch_no: string
  qty: number
  from_location_id: number
  to_location_id: number
  status: PutawayStatus
  created_at: string
  updated_at: string
}

export interface PutawayDetail extends PutawayTask {
  sku_code: string
  sku_name: string
  unit: string
  from_location_code: string
  to_location_code: string
}

export interface AsnDetail {
  asn: Asn
  items: ItemDetail[]
  putaways: PutawayDetail[]
  warehouse_name: string
  supplier_name: string
}

export interface AsnLineInput {
  sku_id: number
  qty: number
}

export interface AsnInput {
  warehouse_id: number
  supplier_id: number
  /** 到货时间，YYYY-MM-DD HH:mm:ss；不填传 null */
  expected_at: string | null
  remark: string
  items: AsnLineInput[]
}

export interface ReceiveLineInput {
  asn_item_id: number
  qty: number
  batch_no: string
  production_date: string | null
  expiry_date: string | null
  location_id: number
}

export interface ReceiveInput {
  items: ReceiveLineInput[]
}

export interface Receipt {
  id: number
  tenant_id: number
  doc_no: string
  asn_id: number
  received_at: string
  operator: string
  created_at: string
}

export interface AsnQuery extends PageQuery {
  keyword?: string
  warehouse?: number
  supplier?: number
  status?: string
}

export interface PutawayQuery extends PageQuery {
  asn_id?: number
  warehouse?: number
  status?: string
}

/** 与后端 services/wms/internal/inbound/model.go 的字典保持一致，改一边必须改另一边 */
export const STATUS_LABELS: Record<InboundStatus, string> = {
  draft: '草稿',
  released: '已释放',
  receiving: '收货中',
  received: '已收货',
  closed: '已关闭',
  cancelled: '已取消',
}

export const PUTAWAY_STATUS_LABELS: Record<PutawayStatus, string> = {
  pending: '待上架',
  done: '已上架',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<InboundStatus, 'info' | 'warning' | 'primary' | 'success' | 'danger'> = {
  draft: 'info',
  released: 'primary',
  receiving: 'warning',
  received: 'success',
  closed: 'info',
  cancelled: 'danger',
}

export const listAsns = (q: AsnQuery): Promise<PageResult<ListRow>> =>
  api.get<PageResult<ListRow>>('/inbound-asns', { ...q })

export const getAsn = (id: number): Promise<AsnDetail> => api.get<AsnDetail>(`/inbound-asns/${id}`)

export const createAsn = (input: AsnInput): Promise<Asn> => api.post<Asn>('/inbound-asns', input)

export const updateAsn = (id: number, input: AsnInput): Promise<Asn> =>
  api.put<Asn>(`/inbound-asns/${id}`, input)

export const deleteAsn = (id: number): Promise<void> => api.del<void>(`/inbound-asns/${id}`)

export const releaseAsn = (id: number): Promise<void> =>
  api.post<void>(`/inbound-asns/${id}/release`, {})

export const cancelAsn = (id: number): Promise<void> =>
  api.post<void>(`/inbound-asns/${id}/cancel`, {})

export const closeAsn = (id: number): Promise<void> =>
  api.post<void>(`/inbound-asns/${id}/close`, {})

export const receiveAsn = (id: number, input: ReceiveInput): Promise<Receipt> =>
  api.post<Receipt>(`/inbound-asns/${id}/receive`, input)

export const listPutaway = (q: PutawayQuery): Promise<PageResult<PutawayDetail>> =>
  api.get<PageResult<PutawayDetail>>('/putaway-tasks', { ...q })

export const confirmPutaway = (taskId: number, toLocationId: number): Promise<PutawayTask> =>
  api.post<PutawayTask>(`/putaway-tasks/${taskId}/confirm`, { to_location_id: toLocationId })
